import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Save, Eye, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Division26DetailHeader } from './shared/Division26DetailHeader';
import { panelFormConfig } from './panelConfig';
import { LocationSelect } from './shared/LocationSelect';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

const BREAKER_TRIP_OPTIONS = ['','15','20','30','40','50','60','70','80','100','125','150','200','225','250','300','400','600'];
const POLES_OPTIONS = ['','1','2','3'];

const HEADER_FIELDS = panelFormConfig.fields.filter(f => f.key !== 'notes');

/* -- Inline select for dense grid -- */
const GridSelect = ({ value, options, onChange, testId, title }) => (
  <select
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    className="w-full h-7 text-xs bg-white border border-slate-200 rounded focus:border-orange-400 focus:ring-1 focus:ring-orange-400/20 outline-none cursor-pointer px-1 transition-colors hover:border-slate-300"
    data-testid={testId}
    title={title}
  >
    {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
  </select>
);

/* -- Tag select (searchable dropdown for device/equipment tags) -- */
const TagSelect = ({ value, options, onChange, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(o => !search || o.toLowerCase().includes(search.toLowerCase()));

  const handleSelect = (val) => {
    onChange(val);
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <input
          value={open ? search : (value || '')}
          onChange={e => {
            if (open) setSearch(e.target.value);
          }}
          onFocus={() => { setOpen(true); setSearch(''); }}
          readOnly={!open}
          placeholder={placeholder}
          className="w-full h-7 text-xs bg-white border border-slate-200 rounded focus:border-orange-400 focus:ring-1 focus:ring-orange-400/20 outline-none px-1.5 transition-colors hover:border-slate-300 cursor-pointer"
          data-testid={testId}
        />
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-52 max-h-40 overflow-y-auto text-xs"
        align="start"
        sideOffset={2}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className={`px-2 py-1.5 cursor-pointer hover:bg-slate-100 text-slate-400 ${!value ? 'bg-slate-50 font-medium' : ''}`}
          onClick={() => handleSelect('')}
        >
          — None —
        </div>
        {filtered.length > 0 ? (
          filtered.map(o => (
            <div
              key={o}
              className={`px-2 py-1.5 cursor-pointer hover:bg-slate-100 ${o === value ? 'bg-orange-50 text-orange-700 font-medium' : ''}`}
              onClick={() => handleSelect(o)}
            >
              {o}
            </div>
          ))
        ) : (
          <div className="px-2 py-1.5 text-slate-400 italic">No results</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/* -- Numeric input for KVA loads -- */
const KvaInput = ({ value, onChange, testId, placeholder = '0.0' }) => {
  const handleKeyDown = (e) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(e.key)) return;
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
    if (e.key === '.') return;
    if (/^[0-9]$/.test(e.key)) return;
    e.preventDefault();
  };
  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') { onChange(null); return; }
    const sanitized = raw.replace(/[^0-9.]/g, '');
    const parts = sanitized.split('.');
    const clean = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
    onChange(clean === '' ? null : parseFloat(clean));
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="w-full h-7 text-xs bg-white border border-slate-200 rounded focus:border-orange-400 focus:ring-1 focus:ring-orange-400/20 outline-none px-1 transition-colors hover:border-slate-300"
      data-testid={testId}
    />
  );
};

/* -- Compact inline location input for circuit grid -- */
const CompactLocationInput = ({ value, onChange, locations, onAddLocation, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = locations.filter(l => !search || l.name.toLowerCase().includes(search.toLowerCase()));
  const showAddNew = search.trim() && !locations.some(l => l.name.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (loc) => {
    onChange(loc.name, loc.id);
    setSearch('');
    setOpen(false);
  };

  const handleAddNew = () => {
    const name = search.trim();
    if (name) {
      onAddLocation(name).then(newLoc => {
        handleSelect(newLoc || { name, id: '' });
      });
    }
  };

  const showPopover = open && (filtered.length > 0 || showAddNew);

  return (
    <Popover open={showPopover} onOpenChange={(o) => {
      if (!o) {
        setOpen(false);
        if (search.trim()) onChange(search.trim(), '');
      }
    }}>
      <PopoverTrigger asChild>
        <input
          value={open ? search : (value || '')}
          onChange={e => {
            if (open) {
              setSearch(e.target.value);
            } else {
              onChange(e.target.value, '');
            }
          }}
          onFocus={() => { setOpen(true); setSearch(''); }}
          placeholder="Location"
          className="w-full h-7 text-xs text-slate-600 bg-white border border-slate-200 rounded focus:border-orange-400 focus:ring-1 focus:ring-orange-400/20 outline-none px-1.5 transition-colors hover:border-slate-300"
          data-testid={testId}
        />
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-48 max-h-32 overflow-y-auto text-xs"
        align="start"
        sideOffset={2}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {filtered.map(loc => (
          <div
            key={loc.id}
            className={`px-2 py-1.5 cursor-pointer hover:bg-slate-100 ${loc.name === value ? 'bg-slate-50 font-medium' : ''}`}
            onClick={() => handleSelect(loc)}
          >
            {loc.name}
          </div>
        ))}
        {showAddNew && (
          <div
            className="px-2 py-1.5 cursor-pointer hover:bg-orange-50 text-orange-600 font-medium border-t"
            onClick={handleAddNew}
          >
            + Add: &quot;{search.trim()}&quot;
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/* -- Quantity input -- */
const QtyInput = ({ value, onChange, testId }) => {
  const handleKeyDown = (e) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(e.key)) return;
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
    if (/^[0-9]$/.test(e.key)) return;
    e.preventDefault();
  };
  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') { onChange(null); return; }
    const num = parseInt(raw.replace(/[^0-9]/g, ''), 10);
    onChange(isNaN(num) ? null : num);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder="0"
      className="w-full h-7 text-xs bg-white border border-slate-200 rounded focus:border-orange-400 focus:ring-1 focus:ring-orange-400/20 outline-none px-1 text-center transition-colors hover:border-slate-300"
      data-testid={testId}
    />
  );
};

/**
 * Circuit Schedule Editor — Symmetric electrical panel schedule.
 * Phase-aware: 3-phase shows KVA A/B/C, 1-phase shows single KVA.
 */
export const CircuitScheduleEditor = ({
  panel,
  projectId,
  manufacturers,
  onBack,
  onReview,
  initialReviewOpen,
  embedded = false,
  onSaveSuccess,
  onDirtyChange,
}) => {
  const [circuits, setCircuits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [deviceTags, setDeviceTags] = useState([]);
  const [equipmentTags, setEquipmentTags] = useState([]);
  const [confirmState, setConfirmState] = useState({ open: false });
  const initialCircuitsRef = useRef(null);

  const totalCircuits = panel?.circuits || 0;
  const totalRows = Math.ceil(totalCircuits / 2);

  // Fetch project locations for autocomplete
  const fetchLocations = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-systems/${projectId}/locations`);
      setLocations(res.data);
    } catch { /* silent */ }
  }, [projectId]);

  // Fetch device tags for the project
  const fetchDeviceTags = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-devices/${projectId}`);
      const tags = [...new Set(res.data.map(d => d.deviceTag).filter(Boolean))].sort();
      setDeviceTags(tags);
    } catch { /* silent */ }
  }, [projectId]);

  // Fetch equipment tags for the project
  const fetchEquipmentTags = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-equipment/${projectId}`);
      const tags = [...new Set(res.data.map(e => e.tag).filter(Boolean))].sort();
      setEquipmentTags(tags);
    } catch { /* silent */ }
  }, [projectId]);

  useEffect(() => {
    fetchLocations();
    fetchDeviceTags();
    fetchEquipmentTags();
  }, [fetchLocations, fetchDeviceTags, fetchEquipmentTags]);

  const handleAddLocation = async (name) => {
    try {
      const res = await axios.post(`${API}/electrical-systems/${projectId}/locations`, { name });
      const newLoc = { id: res.data.id, name: res.data.name || name };
      setLocations(prev => [...prev, newLoc].sort((a, b) => a.name.localeCompare(b.name)));
      return newLoc;
    } catch { return { id: '', name }; }
  };

  const emptyCircuit = (num) => ({
    circuitNumber: num, deviceTag: '', equipmentTag: '', quantity: null, breakerTrip: '',
    poles: '', room: '', roomLocationId: '', kvaLoad: null,
  });

  const initCircuits = useCallback(() => {
    const slots = [];
    for (let i = 1; i <= totalCircuits; i++) slots.push(emptyCircuit(i));
    return slots;
  }, [totalCircuits]);

  const fetchCircuits = useCallback(async () => {
    if (!panel?.id || !projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-panels/${projectId}/${panel.id}/circuits`);
      const saved = res.data;
      const slots = initCircuits();
      const map = {};
      saved.forEach(s => { map[s.circuitNumber] = s; });
      slots.forEach(slot => {
        const s = map[slot.circuitNumber];
        if (s) {
          slot.deviceTag = s.deviceTag || '';
          slot.equipmentTag = s.equipmentTag || '';
          slot.quantity = s.quantity ?? null;
          slot.breakerTrip = s.breakerTrip || '';
          slot.poles = s.poles || '';
          slot.room = s.room || '';
          slot.kvaLoad = s.kvaLoad ?? null;
        }
      });
      setCircuits(slots);
      initialCircuitsRef.current = JSON.stringify(slots);
    } catch {
      const empty = initCircuits();
      setCircuits(empty);
      initialCircuitsRef.current = JSON.stringify(empty);
    } finally {
      setLoading(false);
    }
  }, [panel?.id, projectId, initCircuits]);

  useEffect(() => { fetchCircuits(); }, [fetchCircuits]);

  // Auto-open review modal on URL restore after circuits load
  const reviewTriggered = React.useRef(false);
  useEffect(() => {
    if (initialReviewOpen && !loading && circuits.length > 0 && !reviewTriggered.current) {
      reviewTriggered.current = true;
      onReview(circuits);
    }
  }, [initialReviewOpen, loading, circuits, onReview]);

  const handleChange = useCallback((num, field, value) => {
    setCircuits(prev => prev.map(c => c.circuitNumber === num ? { ...c, [field]: value } : c));
    if (errors[num]) setErrors(prev => { const n = { ...prev }; delete n[num]; return n; });
  }, [errors]);

  // Dirty state: compare current circuits with initial snapshot
  const isDirty = useMemo(() => {
    if (!initialCircuitsRef.current) return false;
    return JSON.stringify(circuits) !== initialCircuitsRef.current;
  }, [circuits]);

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Reset handler: clear all circuits to empty state (local only)
  const handleReset = () => {
    setConfirmState({
      open: true,
      title: 'Reset Circuit Schedule',
      description: 'This will clear all fields in the circuit schedule form. No data will be permanently deleted until you click Save. Are you sure you want to reset?',
      confirmText: 'Confirm Reset',
      variant: 'destructive',
      onConfirm: () => {
        setCircuits(initCircuits());
        setErrors({});
        setConfirmState({ open: false });
        toast.info('Circuit schedule form has been reset');
      },
    });
  };

  const validate = () => {
    const errs = {};
    circuits.forEach(c => {
      const hasAny = (c.deviceTag && c.deviceTag.trim()) || (c.equipmentTag && c.equipmentTag.trim()) ||
        (c.quantity != null && c.quantity > 0) || c.breakerTrip || c.poles || (c.room && c.room.trim()) || c.kvaLoad != null;
      if (hasAny) {
        const msgs = [];
        if (!c.poles) msgs.push('Poles');
        if (msgs.length) errs[c.circuitNumber] = msgs;
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fill required fields for active circuits');
      return;
    }
    setSaving(true);
    try {
      const payload = { circuits: circuits.map(c => ({
        circuitNumber: c.circuitNumber,
        deviceTag: c.deviceTag || null,
        equipmentTag: c.equipmentTag || null,
        quantity: c.quantity,
        breakerTrip: c.breakerTrip || null,
        poles: c.poles || null,
        room: c.room || null,
        kvaLoad: c.kvaLoad,
      })) };
      const res = await axios.put(`${API}/electrical-panels/${projectId}/${panel.id}/circuits`, payload);
      toast.success(`Circuit schedule saved (${res.data.saved} circuits)`);
      initialCircuitsRef.current = JSON.stringify(circuits);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => {
    const result = [];
    for (let r = 0; r < totalRows; r++) {
      const lN = r * 2 + 1;
      const rN = r * 2 + 2;
      result.push({
        left: circuits.find(c => c.circuitNumber === lN) || null,
        right: rN <= totalCircuits ? circuits.find(c => c.circuitNumber === rN) || null : null,
      });
    }
    return result;
  }, [circuits, totalRows, totalCircuits]);

  // KVA totals
  const totals = useMemo(() => {
    let single = 0;
    circuits.forEach(c => { single += c.kvaLoad || 0; });
    return { single };
  }, [circuits]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  /* -- Render one side of a row (left or right) -- */
  const renderSide = (circ, side) => {
    if (!circ) {
      return Array.from({ length: 7 }).map((_, i) => (
        <td key={`empty-${side}-${i}`} className="px-1 py-1 bg-slate-50/50" />
      ));
    }
    const n = circ.circuitNumber;
    const hasErr = errors[n];
    const errCls = hasErr ? 'bg-red-50/70' : '';

    const deviceTagCell = (
      <td key={`dtag-${n}`} className={`px-1 py-1 ${errCls}`} style={{ minWidth: 90 }}>
        <TagSelect
          value={circ.deviceTag}
          options={deviceTags}
          onChange={v => handleChange(n, 'deviceTag', v)}
          placeholder="Device Tag"
          testId={`c${n}-device-tag`}
        />
      </td>
    );

    const equipTagCell = (
      <td key={`etag-${n}`} className={`px-1 py-1 ${errCls}`} style={{ minWidth: 90 }}>
        <TagSelect
          value={circ.equipmentTag}
          options={equipmentTags}
          onChange={v => handleChange(n, 'equipmentTag', v)}
          placeholder="Equip Tag"
          testId={`c${n}-equip-tag`}
        />
      </td>
    );

    const locationCell = (
      <td key={`loc-${n}`} className={`px-1 py-1 ${errCls}`} style={{ minWidth: 90 }}>
        <CompactLocationInput
          value={circ.room || ''}
          onChange={(name, id) => { handleChange(n, 'room', name); if (id) handleChange(n, 'roomLocationId', id); }}
          locations={locations}
          onAddLocation={handleAddLocation}
          testId={`c${n}-room`}
        />
      </td>
    );

    const qtyCell = (
      <td key={`qty-${n}`} className={`px-1 py-1 ${errCls}`} style={{ width: 48 }}>
        <QtyInput value={circ.quantity} onChange={v => handleChange(n, 'quantity', v)} testId={`c${n}-qty`} />
      </td>
    );

    const kvaCell = (
      <td key={`kva-${n}`} className={`px-1 py-1 ${errCls}`} style={{ width: 60 }}>
        <KvaInput value={circ.kvaLoad} onChange={v => handleChange(n, 'kvaLoad', v)} testId={`c${n}-kva`} />
      </td>
    );

    const breakerCell = (
      <td key={`trip-${n}`} className={`px-1 py-1 ${errCls}`} style={{ width: 60 }}>
        <GridSelect value={circ.breakerTrip} options={BREAKER_TRIP_OPTIONS} onChange={v => handleChange(n, 'breakerTrip', v)} testId={`c${n}-trip`} title="Breaker" />
      </td>
    );

    const polesCell = (
      <td key={`poles-${n}`} className={`px-1 py-1 ${errCls}`} style={{ width: 48 }}>
        <GridSelect value={circ.poles} options={POLES_OPTIONS} onChange={v => handleChange(n, 'poles', v)} testId={`c${n}-poles`} title="Poles" />
      </td>
    );

    if (side === 'left') {
      return [locationCell, deviceTagCell, equipTagCell, qtyCell, kvaCell, breakerCell, polesCell];
    } else {
      return [polesCell, breakerCell, kvaCell, qtyCell, equipTagCell, deviceTagCell, locationCell];
    }
  };

  return (
    <div data-testid="circuit-schedule-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <Button size="sm" onClick={onBack} data-testid="circuit-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Back to Panels
          </Button>
        )}
        {embedded && <div />}
        <div className="flex items-center gap-2">
          {embedded && (
            <Button variant="outline" size="sm" onClick={handleReset} className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 transition-colors" data-testid="circuit-reset-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" />Reset
            </Button>
          )}
          {!embedded && (
            <>
              <Button variant="outline" size="sm" onClick={handleReset} className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 transition-colors" data-testid="circuit-reset-btn">
                <RotateCcw className="h-4 w-4 mr-1.5" />Reset
              </Button>
              <Button variant="outline" size="sm" onClick={() => onReview(circuits)} data-testid="circuit-review-btn" className="text-slate-600 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors">
                <Eye className="h-4 w-4 mr-1.5" />Review
              </Button>
            </>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="circuit-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save Changes
          </Button>
        </div>
      </div>

      {/* Panel Summary */}
      {!embedded && (
        <Division26DetailHeader item={panel} fields={HEADER_FIELDS} manufacturers={manufacturers} qrToken={panel?.qrToken} itemName={panel?.name} />
      )}

      {/* Schedule Table */}
      <div className="mt-4 overflow-x-auto border rounded-lg overflow-hidden" data-testid="circuit-grid">
        <table className="w-full border-collapse text-xs" style={{ minWidth: 1060 }}>
          {/* Header */}
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100 border-b">
              {/* Left header */}
              <th className="px-1 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ minWidth: 90 }}>Location</th>
              <th className="px-1 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ minWidth: 90 }}>Device Tag</th>
              <th className="px-1 py-2.5 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ minWidth: 90 }}>Equip Tag</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 48 }}>Qty</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 60 }}>KVA</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 60 }}>Breaker</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 48 }}>Poles</th>
              {/* Center spine */}
              <th className="px-1 py-2.5 text-center font-bold text-xs bg-slate-500 text-white" style={{ width: 38 }}>CKT</th>
              <th className="px-0 py-2.5 text-center font-bold text-[10px] bg-slate-500 text-orange-300" style={{ width: 26 }}></th>
              <th className="px-1 py-2.5 text-center font-bold text-xs bg-slate-500 text-white" style={{ width: 38 }}>CKT</th>
              {/* Right header (mirrored) */}
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 48 }}>Poles</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 60 }}>Breaker</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 60 }}>KVA</th>
              <th className="px-1 py-2.5 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ width: 48 }}>Qty</th>
              <th className="px-1 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ minWidth: 90 }}>Equip Tag</th>
              <th className="px-1 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ minWidth: 90 }}>Device Tag</th>
              <th className="px-1 py-2.5 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide" style={{ minWidth: 90 }}>Location</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const leftNum = row.left?.circuitNumber;
              const rightNum = row.right?.circuitNumber;
              const stripe = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';
              return (
                <tr key={idx} className={`${stripe} border-b border-slate-100 transition-colors`}>
                  {/* Left side */}
                  {renderSide(row.left, 'left')}
                  {/* Center spine */}
                  <td className="text-center font-bold text-xs bg-slate-500 text-white" style={{ width: 38 }}>
                    {leftNum}
                  </td>
                  <td className="bg-slate-500 relative" style={{ width: 26 }}>
                    <div className="flex flex-col items-center justify-center h-full">
                      <span className="w-1.5 h-3 rounded-sm bg-orange-400" />
                    </div>
                  </td>
                  <td className="text-center font-bold text-xs bg-slate-500 text-white" style={{ width: 38 }}>
                    {rightNum || ''}
                  </td>
                  {/* Right side */}
                  {renderSide(row.right, 'right')}
                </tr>
              );
            })}
          </tbody>
          {/* Totals row */}
          <tfoot>
            <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
              <td colSpan={4} className="px-2 py-2.5 text-right text-xs text-slate-600">TOTAL KVA</td>
              <td className="px-1 py-2.5 text-center text-xs text-slate-900">{totals.single.toFixed(1)}</td>
              <td colSpan={2} className="px-1 py-2.5" />
              <td colSpan={3} className="px-1 py-2.5 text-center text-xs bg-slate-500 text-orange-300 font-bold">
                Total: {totals.single.toFixed(1)}
              </td>
              <td colSpan={2} className="px-1 py-2.5" />
              <td className="px-1 py-2.5 text-center text-xs text-slate-900">{totals.single.toFixed(1)}</td>
              <td colSpan={4} className="px-2 py-2.5 text-left text-xs text-slate-600">TOTAL KVA</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Error summary */}
      {Object.keys(errors).length > 0 && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="circuit-errors">
          <p className="text-xs font-medium text-red-700 mb-1">Validation errors:</p>
          {Object.entries(errors).slice(0, 5).map(([num, fields]) => (
            <p key={num} className="text-xs text-red-600">Circuit #{num}: {fields.join(', ')} required</p>
          ))}
        </div>
      )}

      {/* Bottom save for long schedules */}
      {totalRows > 10 && (
        <div className="flex justify-end mt-4">
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="circuit-save-btn-bottom">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save Changes
          </Button>
        </div>
      )}

      <ConfirmDialog {...confirmState} onClose={() => setConfirmState({ open: false })} />
    </div>
  );
};
